import { useCallback, useEffect, useState } from 'react';
import { BiVideo } from 'react-icons/bi';
import { FaPlus } from 'react-icons/fa6';
import { IoImageOutline } from 'react-icons/io5';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

import {
  backupLink,
  coverLink,
  isMediaPending,
  mapMediaVariants,
  MEDIA_POLL_INTERVAL,
  imageVariantLink,
} from '@/common/media';
import { GlassButton } from '@/components';
import { AddToGalleryModal } from '@/components/AddToGalleryModal/AddToGalleryModal';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { MediaPlayBadge } from '@/components/MediaPlayBadge/MediaPlayBadge';
import { Skeleton } from '@/components/Skeleton';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { BaseWidget } from '@/components/Widget/BaseWidget';
import type { GalleryImage } from '@/store/events/events.dto';
import { useEvents } from '@/store/events/events.store';
import { useVessels } from '@/store/vessels/vessels.store';

import styles from './OurMemoriesWidget.module.scss';

interface OurMemoriesWidgetProps {
  onSeeAll?: () => void;
}

function OurMemoriesWidget({ onSeeAll }: OurMemoriesWidgetProps) {
  const getVesselGallery = useEvents((s) => s.getVesselGallery);
  const vesselId = useVessels((s) => s.vessel?.id);
  // `null` until the gallery has answered, so the widget holds the photo's square
  // instead of drawing "No memories yet" and then growing when the photos arrive.
  const [images, setImages] = useState<GalleryImage[] | null>(null);
  const [index, setIndex] = useState(0);
  const [loadedVesselId, setLoadedVesselId] = useState(vesselId);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [previewed, setPreviewed] = useState<GalleryImage | null>(null);

  if (loadedVesselId !== vesselId) {
    setLoadedVesselId(vesselId);
    setImages(null);
    setIndex(0);
  }

  const reloadGallery = useCallback(async () => {
    if (!vesselId) return;
    try {
      setImages(await getVesselGallery());
    } catch (error) {
      console.error(error);
    }
  }, [getVesselGallery, vesselId]);

  useEffect(() => {
    if (!vesselId) return;

    let cancelled = false;

    getVesselGallery()
      .then((gallery) => {
        if (!cancelled) setImages(gallery);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setImages([]);
      });

    return () => {
      cancelled = true;
    };
  }, [getVesselGallery, vesselId]);

  // Activity tells nobody when the encoder is done with a video, so while one is
  // still being processed the gallery is read again until none is — that is what
  // turns "Processing" into the video's cover without a reload.
  const hasPending = images?.some((m) => isMediaPending(m.media_status)) ?? false;
  useEffect(() => {
    if (!hasPending) return;
    const timer = window.setInterval(() => void reloadGallery(), MEDIA_POLL_INTERVAL);
    return () => window.clearInterval(timer);
  }, [hasPending, reloadGallery]);

  const isLoading = images === null;
  const image = images?.[index];
  const total = images?.length ?? 0;

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  const seeAllAction = (
    <button className={styles.seeAllBtn} onClick={onSeeAll}>
      All Memories <MdChevronRight />
    </button>
  );

  return (
    <BaseWidget
      headerIcon={<IoImageOutline />}
      headerTitle="Our Memories"
      headerAction={seeAllAction}
    >
      <div className={styles.content} aria-busy={isLoading || undefined}>
        {isLoading ? (
          // The loaded markup, bone for bone: the photo, its name and date, and the
          // prev / add / next row — direct children of `.content`, so the photo shrinks
          // to fit the widget exactly as the real one does.
          <>
            {/* `height="auto"` lifts the bone's fixed 16px so the wrapper's square holds. */}
            <Skeleton variant="block" height="auto" className={styles.imageWrapper} />
            <div className={styles.info}>
              <Skeleton variant="text" width={120} height={18} />
              <Skeleton variant="text" width={140} height={14} />
            </div>
            <div className={styles.nav}>
              <Skeleton variant="block" width={32} height={32} className={styles.navBone} />
              <Skeleton variant="block" width={32} height={32} className={styles.navBone} />
              <Skeleton variant="block" width={32} height={32} className={styles.navBone} />
            </div>
          </>
        ) : images && image ? (
          <>
            <div className={styles.imageWrapper}>
              <div className={styles.track} style={{ transform: `translateX(-${index * 100}%)` }}>
                {images.map((m) => {
                  // A video shows the cover the encoder took from it under the play mark;
                  // one still processing shows the glyph instead, as nothing plays yet.
                  const isVideo = m.content_type.startsWith('video/');
                  const still = isVideo
                    ? coverLink(mapMediaVariants(m.link, m.variants))
                    : imageVariantLink(m, 300);
                  const pending = isMediaPending(m.media_status);

                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={styles.slide}
                      onClick={() => setPreviewed(m)}
                      aria-label={`Open ${m.name}`}
                    >
                      {still ? (
                        <>
                          {/* Under the photo while its private link resolves —
                              `AuthorizedImage` draws nothing until then. */}
                          <Skeleton variant="block" className={styles.slideBone} />
                          <AuthorizedImage src={still} alt={m.name} className={styles.image} />
                        </>
                      ) : (
                        pending && (
                          <BiVideo size={40} aria-hidden="true" className={styles.videoGlyph} />
                        )
                      )}
                      {isVideo && !pending && <MediaPlayBadge overlay />}
                      {pending && (
                        <StatusPill className={styles.status} label="Processing" tone="info" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={styles.info}>
              <span className={styles.title}>{image.name.replace(/\.[^.]+$/, '')}</span>
              <span className={styles.date}>
                {new Date(image.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className={styles.nav}>
              <GlassButton size={32} shape="square" onClick={prev} aria-label="Previous memory">
                <MdChevronLeft size={20} />
              </GlassButton>
              <GlassButton size={32} shape="square" onClick={() => setIsAddOpen(true)}>
                <FaPlus size={16} />
              </GlassButton>
              {/*<button*/}
              {/*  style={{*/}
              {/*    cursor: 'pointer',*/}
              {/*    padding: '0 24px',*/}
              {/*    borderRadius: 8,*/}
              {/*    border: 'var(--button-border)',*/}
              {/*    background: 'var(--button-bg)',*/}
              {/*    fontSize: 16,*/}
              {/*    fontFamily: 'var(--font-inter), sans-serif',*/}
              {/*    color: '#fff',*/}
              {/*    display: 'flex',*/}
              {/*    alignItems: 'center',*/}
              {/*    justifyContent: 'center',*/}
              {/*    gap: 0,*/}
              {/*    fontWeight: 700,*/}
              {/*    backdropFilter: 'blur(25px)',*/}
              {/*    boxShadow: '4px 4px 4px 2px rgba(29, 61, 88, 0.3019607843)',*/}
              {/*    height: 31,*/}
              {/*  }}*/}
              {/*  onClick={onModalOpen}*/}
              {/*>*/}
              {/*  <FaPlus size={20} style={{ marginRight: 4 }} /> Add*/}
              {/*</button>*/}
              <GlassButton size={32} shape="square" onClick={next} aria-label="Next memory">
                <MdChevronRight size={20} />
              </GlassButton>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <span className={styles.empty}>Please add a memory to get started.</span>
            <GlassButton size={32} shape="square" onClick={() => setIsAddOpen(true)}>
              <FaPlus size={16} />
            </GlassButton>
          </div>
        )}
      </div>

      {/* The preview plays a video — or says it is still being processed and plays
          it once it is ready. */}
      {previewed && (
        <DocumentPreviewModal
          document={{
            name: previewed.name,
            contentType: previewed.content_type,
            storageLink: previewed.link,
            size: previewed.size,
            uploadedAt: previewed.created_at,
            mediaStatus: previewed.media_status,
            backupLink: backupLink(mapMediaVariants(previewed.link, previewed.variants)),
          }}
          onClose={() => setPreviewed(null)}
        />
      )}

      <AddToGalleryModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onUploaded={() => void reloadGallery()}
      />
    </BaseWidget>
  );
}

export default OurMemoriesWidget;
